import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { CheckCircle2, Circle } from 'lucide-react';
import React, { useMemo } from 'react';

/**
 * Password strength meter — DESIGN-SYSTEM.md §5.12.
 *
 * Canonical reference implementation is the "Segurança" tab in
 * `prototipo-configuracoes.html` (`checkStrength()`): 4 equally-weighted
 * criteria (25% each), track/label color stepping through
 * danger -> warning -> brand -> success as more criteria are met, plus a
 * live checklist with filled/outline circle-check per criterion.
 *
 * Shared by `ChangePassword.js` and `Login.js` (cadastro) per
 * FRONTEND-TASKS.md §1 — one component, not a re-implementation per screen.
 */
export function getPasswordChecks(password) {
    const value = password || '';
    return {
        length: value.length >= 8,
        upper: /[A-Z]/.test(value),
        number: /[0-9]/.test(value),
        special: /[^A-Za-z0-9]/.test(value),
    };
}

const CRITERIA = [
    { key: 'length', label: 'Pelo menos 8 caracteres' },
    { key: 'upper', label: 'Letra maiúscula' },
    { key: 'number', label: 'Número' },
    { key: 'special', label: 'Caractere especial' },
];

export default function PasswordStrengthMeter({ password, emptyLabel = 'Digite a nova senha', showChecklist = true, sx }) {
    const theme = useTheme();
    const checks = useMemo(() => getPasswordChecks(password), [password]);

    const score = Object.values(checks).filter(Boolean).length; // 0-4
    const percent = score * 25;
    const value = password || '';

    const tiers = [
        { color: theme.palette.text.disabled, label: emptyLabel },
        { color: theme.palette.error.main, label: 'Muito fraca' },
        { color: theme.palette.warning.main, label: 'Média' },
        { color: theme.palette.primary.main, label: 'Forte' },
        { color: theme.palette.success.main, label: 'Muito forte' },
    ];
    const idx = value.length === 0 ? 0 : score;
    const tier = tiers[idx];

    return (
        <Box sx={sx}>
            <Box sx={{ height: 8, borderRadius: '6px', backgroundColor: 'background.subtle', overflow: 'hidden', mt: '6px', mb: '4px' }}>
                <Box sx={{ height: '100%', borderRadius: '6px', width: `${percent}%`, backgroundColor: tier.color, transition: 'width .25s ease, background-color .25s ease' }} />
            </Box>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: tier.color, display: 'block', mb: showChecklist ? 1 : 0 }}>
                {tier.label}
            </Typography>
            {showChecklist && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
                    {CRITERIA.map((c) => {
                        const ok = checks[c.key];
                        const Icon = ok ? CheckCircle2 : Circle;
                        return (
                            <Box key={c.key} sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: ok ? 'success.main' : 'text.disabled' }}>
                                <Icon size={13} strokeWidth={2.5} />
                                {c.label}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
